/**
 * usePaywall — the blocking-fact + top-up composition for the paywall
 * surfaces (Plan 02 D3). One hook answers everything an app's paywall UI
 * needs for one service, so no application ever derives limits, overage or
 * pack choices itself (the D3 rule: apps consume snapshots, the server stays
 * the enforcement point):
 *
 *   - `meters`        the service's meters, in the usage answer's order
 *   - `meter`         the primary consumption meter (first non-policy row)
 *   - `blocked`       the meter at or past its threshold — the overage state
 *   - `fileCapMeter`  the plan's per-file cap (`max_file_bytes`, limit-only)
 *   - `pack`          the service's cheapest one_time top-up pack — null when
 *                     the catalog sells none for this service yet (the CTA
 *                     stays display-only; the UI never invents a product)
 *   - `upgradePlan`   the cheapest subscription tier above the caller's
 *                     current rank — the upgrade path for services that sell
 *                     a tier rather than a pack (tts, Plan 03 A3)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { billingClient, BillingError } from "../client/billingClient";
import type { Plan, UsageMeterData } from "../types";
import { billingCacheKeys, cachedRead, invalidateBillingCache, readCached } from "./billingCache";
import { useEntitlement, useUsage } from "./useEntitlement";

export interface PaywallState {
  meters: UsageMeterData[];
  meter: UsageMeterData | null;
  blocked: UsageMeterData | null;
  fileCapMeter: UsageMeterData | null;
  pack: Plan | null;
  upgradePlan: Plan | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/** The blocking facts + pack for one billable service. */
export function usePaywall(serviceId: string): PaywallState {
  const { usage, loading, error, refresh: refreshUsage } = useUsage();
  // The current tier's rank gates the upgrade path; the entitlements read is
  // cache-shared with every other kit hook, so this costs no extra request.
  const { snapshot } = useEntitlement(serviceId);
  const [plans, setPlans] = useState<Plan[] | null>(() => readCached<Plan[]>(billingCacheKeys.plans)?.value ?? null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadPlans = useCallback(async () => {
    try {
      const answer = await cachedRead(billingCacheKeys.plans, () => billingClient.getPlans().then((r) => r.plans));
      if (!mounted.current) return;
      setPlans(answer);
    } catch (err) {
      if (!mounted.current) return;
      // Stale-while-error, mirroring the kit's other reads: the last good
      // catalog keeps the surface renderable inside the grace window.
      const cached = readCached<Plan[]>(billingCacheKeys.plans);
      if (cached?.staleOk) {
        setPlans(cached.value);
        return;
      }
      if (err instanceof BillingError && err.status === 401) {
        // Signed out (or a session the app origin cannot see): the paywall
        // stays neutral — no facts, no affordance.
        setPlans(null);
        return;
      }
      setPlans([]);
    }
  }, []);

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  const refresh = useCallback(async () => {
    invalidateBillingCache(billingCacheKeys.plans);
    await Promise.all([refreshUsage(), loadPlans()]);
  }, [refreshUsage, loadPlans]);

  const service = usage?.services.find((entry) => entry.serviceId === serviceId) ?? null;
  const meters = service && !service.unavailable ? service.meters : [];
  const meter = meters.find((row) => row.source !== "policy") ?? null;
  const blocked = meters.find((row) => row.source !== "policy" && row.limit > 0 && row.used >= row.limit) ?? null;
  const fileCapMeter = meters.find((row) => row.key === "max_file_bytes") ?? null;
  const pack = (plans ?? []).find((plan) => plan.serviceId === serviceId && plan.billingCycle === "one_time") ?? null;
  // The subscription upgrade: the cheapest active tier above the caller's
  // current rank. An already-top-tier caller (or an unread snapshot) gets
  // null — the card renders display-only rather than offering a sideways move.
  const currentRank = snapshot?.rank ?? Number.POSITIVE_INFINITY;
  const upgradePlan =
    (plans ?? [])
      .filter((plan) => plan.serviceId === serviceId && plan.billingCycle !== "one_time" && plan.rank > currentRank)
      .sort((a, b) => a.rank - b.rank)[0] ?? null;

  return { meters, meter, blocked, fileCapMeter, pack, upgradePlan, loading, error, refresh };
}
